/**
 * The basket, and the order it becomes (PT-06, V5, docs/15).
 *
 * Quantities as typed, by product, each with the plant as it was listed when
 * it went in. Keeping the plant is what lets a line outlive the plant leaving
 * the list: stock that sold while the basket was open is still asked for, and
 * the nursery decides at confirmation what it can supply (V5). The page says
 * so beside the line rather than quietly dropping it.
 *
 * There is no cap by stock, because the page knows no stock figure. A quantity
 * is any positive number up to 100000 with at most three decimal places - the
 * bounds `portal_place_order` holds a line to - so the page refuses only what
 * the server would refuse anyway.
 */

import {
  type Cents,
  type Quantity,
  cents,
  compareQuantity,
  describeQuantityError,
  formatQuantity,
  isNegativeQuantity,
  isZeroQuantity,
  lineGross,
  parseQuantity,
  quantityFromUnits,
} from '@smf/core';

import type { Catalogue } from './catalogue.js';
import type { ListedItem, PlaceRequest } from './portal-api.js';

/** The most one line may ask for: the server's bound, not a stock figure. */
export const MOST_ON_A_LINE = quantityFromUnits(100_000);

export interface BasketEntry {
  /** The plant as it was listed when the customer chose it. */
  readonly item: ListedItem;
  /** The quantity as typed, checked by `checkBasket`. */
  readonly typed: string;
}

/** By product, in the order the customer chose them. */
export type Basket = ReadonlyMap<string, BasketEntry>;

export const EMPTY_BASKET: Basket = new Map();

function blank(entry: BasketEntry): boolean {
  return entry.typed.trim() === '';
}

/**
 * The basket with one box changed, in its place. An emptied box stays, so a
 * quantity being retyped on the review does not vanish under the customer's
 * thumb; it goes nowhere, and `withoutBlanks` tidies it away.
 */
export function withQuantity(basket: Basket, item: ListedItem, typed: string): Basket {
  const next = new Map(basket);
  next.set(item.productId, { item, typed });
  return next;
}

/** The basket without its emptied boxes, as the review opens. */
export function withoutBlanks(basket: Basket): Basket {
  return new Map([...basket].filter(([, entry]) => !blank(entry)));
}

export function without(basket: Basket, productId: string): Basket {
  const next = new Map(basket);
  next.delete(productId);
  return next;
}

/** A quantity as the server writes it (`40.000`), as a person reads it: `40`. */
export function readable(quantity: string): string {
  const parsed = parseQuantity(quantity);
  return parsed.ok ? formatQuantity(parsed.value) : '0';
}

export interface BasketLine {
  readonly item: ListedItem;
  readonly quantity: Quantity;
  /** Still in stock when the list was last read. A line that is not is kept. */
  readonly listed: boolean;
  readonly unitPriceCents?: Cents;
  readonly lineCents?: Cents;
}

export interface CheckedBasket {
  /** The lines that can go, in the order they were chosen. */
  readonly lines: readonly BasketLine[];
  /** A sentence per line that cannot go yet, by product. */
  readonly problems: Readonly<Record<string, string>>;
  /** The sum of the priced lines. */
  readonly totalCents: Cents;
  /** Some line has no price yet, so the total is not the whole of it. */
  readonly partlyPriced: boolean;
  /** Plants with something in their box, whether it can go yet or not. */
  readonly chosen: number;
}

/** What is wrong with a typed quantity, or the quantity. */
function quantityOf(
  typed: string,
):
  | { readonly ok: true; readonly value: Quantity }
  | { readonly ok: false; readonly problem: string } {
  const parsed = parseQuantity(typed);
  if (!parsed.ok) return { ok: false, problem: describeQuantityError(parsed.error) };
  if (isZeroQuantity(parsed.value) || isNegativeQuantity(parsed.value)) {
    return { ok: false, problem: 'Choose at least one, or leave it empty.' };
  }
  if (compareQuantity(parsed.value, MOST_ON_A_LINE) > 0) {
    return {
      ok: false,
      problem: `At most ${formatQuantity(MOST_ON_A_LINE)} on one line. Call the nursery for more.`,
    };
  }
  return { ok: true, value: parsed.value };
}

/**
 * The basket as it would be placed, against the latest read of what is in
 * stock. An empty box is not an order line and not a problem. A plant no
 * longer listed stays in, unpriced - its price rows went with it - and marked,
 * so the review can say the nursery will confirm it.
 */
export function checkBasket(basket: Basket, catalogue: Catalogue): CheckedBasket {
  const lines: BasketLine[] = [];
  const problems: Record<string, string> = {};
  let total = 0;
  let partlyPriced = false;
  let chosen = 0;

  for (const [productId, entry] of basket) {
    if (blank(entry)) continue;
    chosen += 1;
    const quantity = quantityOf(entry.typed);
    if (!quantity.ok) {
      problems[productId] = quantity.problem;
      continue;
    }
    const current = catalogue.listed.get(productId);
    const item = current ?? entry.item;
    const listed = current !== undefined;
    const unit = catalogue.prices.get(productId);
    if (unit === undefined) {
      partlyPriced = true;
      lines.push({ item, quantity: quantity.value, listed });
      continue;
    }
    const lineCents = lineGross(unit, quantity.value);
    total += lineCents;
    lines.push({
      item,
      quantity: quantity.value,
      listed,
      unitPriceCents: unit,
      lineCents,
    });
  }

  return { lines, problems, totalCents: cents(total), partlyPriced, chosen };
}

export interface PlaceOptions {
  readonly deliveryAddressId?: string;
  readonly requiredDate?: string;
  readonly note?: string;
}

/**
 * What `portal_place_order` is sent. `id` is kept while the same order is
 * sent again, so pressing Place twice, or again after the connection dropped,
 * is one order; `placing.ts` decides when a changed basket needs a new one.
 * Who is ordering is not in it: the server reads that from the session.
 */
export function placeRequest(
  id: string,
  lines: readonly BasketLine[],
  options: PlaceOptions = {},
): PlaceRequest {
  const note = options.note?.trim();
  return {
    id,
    lines: lines.map((line) => ({
      product_id: line.item.productId,
      quantity: formatQuantity(line.quantity),
      ...(line.unitPriceCents === undefined
        ? {}
        : { shown_unit_price_cents: line.unitPriceCents }),
    })),
    ...(options.deliveryAddressId === undefined
      ? {}
      : { delivery_address_id: options.deliveryAddressId }),
    ...(options.requiredDate === undefined || options.requiredDate === ''
      ? {}
      : { required_date: options.requiredDate }),
    ...(note === undefined || note === '' ? {} : { note }),
  };
}
